import { DocumentReader } from '../../word/api/DocumentReader';
import { insertParagraphsForAgent } from '../../word/api/AgentDocumentInserter';
import { applyWordStructureOps, type StructureOp } from '../../word/api/AgentStructureApplier';
import { applyDocumentRestructureOps, type RestructureOp } from '../../word/api/DocumentRestructurer';
import { findFlattenCandidates } from '../../word/api/FlattenCandidates';
import { validateDocumentRtl } from '../../word/api/wordDocumentRtl';
import { log, logError } from '../../utils/logging';

export type AgentToolCall = { id: string; name: string; arguments: Record<string, unknown> };

export type AgentToolResultPayload = {
  id: string;
  name: string;
  result?: string;
  error?: string;
};

const MAX_FIND = 24;

/**
 * Executes Word-local tool calls requested by the agent loop (selection, paragraph read, search, insert paragraphs).
 * Paragraph indices match `DocumentReader.getFullDocumentContext()` (non-empty paragraphs only).
 */
export async function executeAgentToolCalls(calls: AgentToolCall[]): Promise<AgentToolResultPayload[]> {
  const out: AgentToolResultPayload[] = [];
  for (const tc of calls) {
    try {
      const result = await runOneTool(tc);
      out.push({ id: tc.id, name: tc.name, result });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      logError('[AgentWordTools] tool failed', {
        name: tc.name,
        id: tc.id,
        msg,
        argsKeys: tc.arguments ? Object.keys(tc.arguments) : [],
      });
      out.push({ id: tc.id, name: tc.name, error: msg });
    }
  }
  return out;
}

async function runOneTool(tc: AgentToolCall): Promise<string> {
  const args = tc.arguments || {};
  switch (tc.name) {
    case 'get_selection':
      return await toolGetSelection();
    case 'read_paragraph':
      return await toolReadParagraph(Number(args.index));
    case 'read_paragraphs':
      return await toolReadParagraphs(Number(args.start_index), Number(args.end_index));
    case 'find_text':
      return await toolFindText(String(args.query ?? ''), Number(args.max_results ?? 10));
    case 'insert_paragraphs':
      return await toolInsertParagraphs(args);
    case 'apply_word_structure':
      return await toolApplyWordStructure(args);
    case 'restructure_document':
      return await toolRestructureDocument(args);
    case 'find_flatten_candidates':
      return await toolFindFlattenCandidates();
    case 'validate_document_rtl':
      return await toolValidateDocumentRtl();
    default:
      return JSON.stringify({ error: `Unknown tool: ${tc.name}` });
  }
}

async function toolGetSelection(): Promise<string> {
  const sel = await DocumentReader.getSelectionOrParagraph();
  return JSON.stringify({
    text: sel.text ?? '',
    paragraphIndex: sel.paragraphIndex ?? null,
    isEmpty: sel.isEmpty,
  });
}

async function toolReadParagraph(index: number): Promise<string> {
  if (Number.isNaN(index)) {
    return JSON.stringify({ error: 'Invalid index' });
  }
  const ctx = await DocumentReader.getFullDocumentContext();
  const p = ctx.paragraphs.find((x) => x.index === index);
  return JSON.stringify({
    index,
    text: p?.text ?? '',
    found: !!p,
  });
}

async function toolReadParagraphs(start: number, end: number): Promise<string> {
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return JSON.stringify({ error: 'Invalid range' });
  }
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  const ctx = await DocumentReader.getFullDocumentContext();
  const paragraphs = ctx.paragraphs.filter((p) => p.index >= lo && p.index <= hi);
  return JSON.stringify({
    start_index: lo,
    end_index: hi,
    paragraphs,
  });
}

async function toolFindText(query: string, maxResults: number): Promise<string> {
  const max = Math.min(MAX_FIND, Math.max(1, Number.isFinite(maxResults) ? maxResults : 10));
  const q = query.trim();
  if (!q) {
    return JSON.stringify({ query: q, matches: [] });
  }
  const ctx = await DocumentReader.getFullDocumentContext();
  const matches: { paragraphIndex: number; snippet: string }[] = [];
  for (const p of ctx.paragraphs) {
    let from = 0;
    while (matches.length < max) {
      const pos = p.text.indexOf(q, from);
      if (pos === -1) break;
      const snStart = Math.max(0, pos - 60);
      const snEnd = Math.min(p.text.length, pos + q.length + 60);
      matches.push({ paragraphIndex: p.index, snippet: p.text.slice(snStart, snEnd) });
      from = pos + Math.max(1, q.length);
    }
    if (matches.length >= max) break;
  }
  return JSON.stringify({ query: q, matches });
}

async function toolInsertParagraphs(args: Record<string, unknown>): Promise<string> {
  const raw = args.paragraphs;
  // Accept both plain string arrays and rich ParagraphSpec object arrays
  const paragraphs: (string | import('../../word/api/AgentDocumentInserter').ParagraphSpec)[] = Array.isArray(raw)
    ? raw.map((x) => (x !== null && typeof x === 'object' ? x as import('../../word/api/AgentDocumentInserter').ParagraphSpec : String(x ?? '')))
    : typeof raw === 'string'
      ? raw.split(/\n\n+/).map((s) => s.trim())
      : [];
  const mode = String(args.mode ?? 'fill_empty').trim();
  if (mode !== 'fill_empty' && mode !== 'after_agent_paragraph') {
    return JSON.stringify({ error: `Invalid mode: ${mode}` });
  }
  const afterIdx =
    args.after_agent_paragraph_index !== undefined && args.after_agent_paragraph_index !== null
      ? Number(args.after_agent_paragraph_index)
      : undefined;

  log('[AgentWordTools] insert_paragraphs', {
    mode,
    afterIdx: afterIdx ?? null,
    paragraphCount: paragraphs.length,
    listMeta: paragraphs.slice(0, 12).map((p, i) =>
      typeof p === 'object' && p !== null && 'list' in p
        ? { i, list: (p as { list?: string }).list, level: (p as { level?: number }).level }
        : { i, list: null }
    ),
  });

  const res = await insertParagraphsForAgent(
    paragraphs,
    mode as 'fill_empty' | 'after_agent_paragraph',
    afterIdx
  );
  log('[AgentWordTools] insert_paragraphs result', res);
  return JSON.stringify(res);
}

async function toolValidateDocumentRtl(): Promise<string> {
  const res = await validateDocumentRtl();
  log('[AgentWordTools] validate_document_rtl', res);
  return JSON.stringify(res);
}

async function toolFindFlattenCandidates(): Promise<string> {
  const res = await findFlattenCandidates();
  log('[AgentWordTools] find_flatten_candidates', {
    ok: res.ok,
    paragraph_count: res.paragraph_count,
    candidate_count: res.candidate_count,
    preview: res.candidates.slice(0, 8).map((c) => ({
      parent_index: c.parent_index,
      parent_level: c.parent_level,
      parent_text_snippet: c.parent_text.slice(0, 60),
      children_count: c.children.length,
    })),
  });
  return JSON.stringify(res);
}

async function toolRestructureDocument(args: Record<string, unknown>): Promise<string> {
  const raw = args.operations ?? args.ops;
  const ops = Array.isArray(raw) ? raw : [];
  log('[AgentWordTools] restructure_document', {
    opCount: ops.length,
    opsPreview: ops.slice(0, 8).map((o: unknown) =>
      o && typeof o === 'object' && 'op' in o ? (o as { op: string }).op : typeof o
    ),
  });
  const res = await applyDocumentRestructureOps(ops as RestructureOp[]);
  log('[AgentWordTools] restructure_document result', res);
  return JSON.stringify(res);
}

async function toolApplyWordStructure(args: Record<string, unknown>): Promise<string> {
  const raw = args.operations ?? args.ops;
  const ops = Array.isArray(raw) ? raw : [];
  log('[AgentWordTools] apply_word_structure', {
    opCount: ops.length,
    opsPreview: ops.slice(0, 6).map((o: unknown) =>
      o && typeof o === 'object' && 'op' in o ? (o as { op: string }).op : typeof o
    ),
  });
  const res = await applyWordStructureOps(ops as StructureOp[]);
  log('[AgentWordTools] apply_word_structure result', res);
  return JSON.stringify(res);
}
